import { Dialog, LinearProgress } from '@mui/material'
import { useState } from 'react'
import { sleep } from '../helpers/FormatLinks'
import { getBoostAction } from '../helpers/WaxApi'
import { DropAlert } from './DropAlert'
import { DropPaper } from './DropPaper'
import { useSharedState } from '../waxplorer/Waxplorer'
import MainButton from '../common/util/input/MainButton'

export const RemoveAllAssetsDialog = ({ open, setOpen, drop }) => {
    const MAX_REMOVES_PER_TRANSACTION = 20
    const [state] = useSharedState()

    const activeUser = state?.activeUser

    const userName = activeUser?.accountName
        ? state?.activeUser.accountName
        : null

    const [errors, setErrors] = useState(null)
    const [transactionIds, setTransactionIds] = useState(null)
    const [isRemoving, setIsRemoving] = useState(false)
    const [currentDisplayAction, setCurrentDisplayAction] = useState(0)

    const [assetAmountToRemove, setAssetAmountToRemove] = useState(0)
    const [assetsRemoved, setAssetsRemoved] = useState([])

    const close = () => {
        if (!isRemoving && !errors) {
            setOpen(false)
            setCurrentDisplayAction(0)
        }
    }

    const removeAllAssets = async () => {
        setErrors(null)
        setCurrentDisplayAction(0)
        setTransactionIds(null)
        setIsRemoving(true)

        const tempTransactionIds = []
        const tempErrors = []
        let tempAssetsRemoved = []

        let currentAction = 0

        let assetsRemovedAmount = 0

        const assetIdsToRemoveThisTransaction = drop?.assets?.filter(
            (element) => !assetsRemoved.includes(element),
        )

        const tempAssetAmountToRemove = assetIdsToRemoveThisTransaction.length
        setAssetAmountToRemove(tempAssetAmountToRemove)

        while (assetsRemovedAmount < tempAssetAmountToRemove) {
            const removesThisTransaction = Math.min(
                MAX_REMOVES_PER_TRANSACTION,
                tempAssetAmountToRemove - assetsRemovedAmount,
            )

            const actions = [getBoostAction(activeUser)]

            const assetIds = assetIdsToRemoveThisTransaction.slice(
                assetsRemovedAmount,
                assetsRemovedAmount + removesThisTransaction,
            )

            const remAssetsData = {
                authorized_account: userName,
                drop_id: drop.dropId,
                asset_ids: assetIds,
            }

            const remAssetsAction = {
                account: 'nfthivedrops',
                name: 'remassets',
                authorization: [
                    {
                        actor: userName,
                        permission: activeUser.requestPermission
                            ? activeUser.requestPermission
                            : 'active',
                    },
                ],
                data: remAssetsData,
            }

            actions.push(remAssetsAction)

            let remAssetsError = null
            try {
                const result = await activeUser.session.transact(
                    {
                        actions: actions,
                    },
                    {
                        expireSeconds: 300,
                        blocksBehind: 0,
                    },
                )
                tempTransactionIds.push(result?.response?.transaction_id)
            } catch (e) {
                remAssetsError = e.message
                tempErrors.push(remAssetsError)
            }
            if (!remAssetsError) {
                tempAssetsRemoved = [...tempAssetsRemoved, ...assetIds]
                setAssetsRemoved(tempAssetsRemoved)
            }
            assetsRemovedAmount = assetsRemovedAmount + removesThisTransaction
            currentAction++
            setCurrentDisplayAction(currentAction)
            if (tempAssetAmountToRemove - assetsRemovedAmount > 0) {
                await sleep(2000)
            }
        }
        if (tempTransactionIds.length > 0) {
            await sleep(2000)
            // TODO reload editor state
            setTransactionIds(tempTransactionIds)
        }
        if (tempErrors.length > 0) {
            setErrors(tempErrors)
        }
        setIsRemoving(false)
    }

    return (
        <Dialog open={open} onClose={() => close()} maxWidth="sm" fullWidth>
            <DropPaper noMargin title="Remove All Assets from this pool?">
                <DropAlert
                    severity="warning"
                    title="Caution: This will break your drop!"
                    condition={true}
                >
                    This method is intended as a last resort if{' '}
                    <b>
                        you cannot delete an existing drop because of timeout
                        issues
                    </b>
                    . This will remove all existing assets from the drop pool in
                    small batches, so you can delete your drop afterwards.
                    Removing all assets from a premint drop will break it, no
                    user will be able to claim any more NFTs once you have
                    removed them all!
                </DropAlert>
                <DropAlert
                    condition={errors}
                    title="An error occured during the update of the pool assets of this drop!"
                    severity="error"
                >
                    <div>Error Message:</div>
                    {errors?.map((error) => (
                        <div>{error}</div>
                    ))}
                </DropAlert>
                <DropAlert
                    condition={transactionIds !== null}
                    severity="success"
                    title="Success!"
                >
                    Successfully executed transaction
                    {transactionIds?.length !== 1 ? 's' : ''}
                    {transactionIds?.map((transactionId) => (
                        <div>
                            <a
                                className="font-mono hover:underline"
                                href={
                                    (process.env.NEXT_PUBLIC_TESTNET === 'TRUE'
                                        ? 'https://testnet.waxblock.io/transaction/'
                                        : 'https://waxblock.io/transaction/') +
                                    transactionId
                                }
                                target="_blank"
                            >
                                {transactionId}
                            </a>
                        </div>
                    ))}
                </DropAlert>
                {isRemoving ? (
                    <div className="mt-10">
                        <div className="text-xl italic">
                            Loading Transactions - Adding wait time to avoid
                            Rate Limiting - Tx {currentDisplayAction} /{' '}
                            {Math.ceil(
                                assetAmountToRemove /
                                    MAX_REMOVES_PER_TRANSACTION,
                            )}
                        </div>
                        <div className="my-5">
                            <LinearProgress />
                        </div>
                    </div>
                ) : (
                    <div className="flex flex-wrap justify-center mt-10 flex flex-col">
                        <MainButton
                            className="w-full mb-5"
                            fullWidth={true}
                            onClick={() => removeAllAssets()}
                            color="warning"
                            disabled={transactionIds && !errors}
                        >
                            {errors
                                ? 'Retry Remaining'
                                : 'Yes, remove all assets from this drop'}
                        </MainButton>
                        <MainButton
                            className="w-full"
                            fullWidth={true}
                            onClick={() => close()}
                            color="danger"
                            disabled={errors}
                        >
                            {transactionIds === null
                                ? 'No, cancel and return to overview'
                                : 'Close'}
                        </MainButton>
                    </div>
                )}
            </DropPaper>
        </Dialog>
    )
}
